import { useCallback, useEffect, useMemo, useState } from 'react';
import { NotificationDispatch, RoomType, TextField } from '@tryhackme/thm-ui-components';
import { faLightbulb, faPaperPlane } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useGetUserQuery } from 'src/common/slices';
import { useForm } from 'src/common/form';
import { useFeatureIsOn } from 'src/common/growthbook';
import { QuestionAndAnswerItemProps } from 'src/common/interfaces/question-and-answer-item';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { setIsRoomComplete, updateTaskQuestionState } from 'src/app/reducers/room/room.reducer';
import { replaceContent } from 'src/features/room/helpers/content-replacer';
import { invalidateResetProgress, useGetRoomDetailQuery, useResetUserProgressMutation } from 'src/features/room/room.slice';
import { addSocketListener, removeSocketListener } from 'src/common/sockets';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { MaskedFormInput } from 'src/common/components/masked-form-input';
import { useGetCurrentTeamQuery } from 'src/features/manage-account/teams/team.slice';
import { useOpenTutorWithDelayedAction } from '../tutor/hooks/use-open-tutor-with-delayed-action';
import { displayStreakNotification } from '../streak-notification/helpers';
import { QuestionHint } from '../question-hint/question-hint';
import { TutorSocketEventType } from '../tutor/tutor.types';
import { RoomTeamEvent } from '../../room.types';
import { ROOM_TEAM_EVENT } from '../../room.constants';
import { useIsEchoEnabledInCurrentRoom } from '../../room.helpers';
import { detectLanguage } from '../../helpers/language-detection';
import { StyledButton, StyledButtonsContainer, StyledContainer, StyledExtraPointsTagAndQuestionContainer, StyledForm, StyledHintButton, StyledPoints, StyledQuestion, StyledQuestionContainer, StyledTag, StyledTagContainer, StyledTextfieldContainer, StyledAutoAnswerContainer } from './question-and-answer-item.styles';
import { usePostAnswerMutation } from './question-and-answer-item.slice';
import { AnswerErrorMessage, AnswerPlaceholder, SubmitButtonTitle, QUESTION_OPTIONS } from './question-and-answer-item.constants';
import { getQuestionOptions, isSameAnswer } from './question-and-answer-item.helpers';
import { RadioInputGroup } from './radio-input-group/radio-input-group';
import { StyledRadioSelectionWrapper } from './radio-input-group/radio-input-group.styles';
import { AutoAnswerIcon } from './auto-answer-icon';
import { AutoAnswerLabel } from './auto-answer-label';
import { useAnswerMask } from './hooks';
const successNotification = () => NotificationDispatch('success', 'Woop woop! Your answer is correct');
const errorNotification = () => NotificationDispatch('error', 'Uh-oh! Your answer is incorrect');
export const QuestionAndAnswerItem = ({
  question,
  hint,
  questionNo,
  taskId,
  joinRoom,
  hasUserJoinedRoom,
  machineIP,
  attackBoxPrivateIP,
  roomCode,
  roomType,
  autoAnswerQuestions,
  autoAnswerLabel,
  isFirstNonAnsweredQuestion,
  advanceToNextTask,
  isTaskBreakdownSupported,
  isLastTask,
  ...props
}: QuestionAndAnswerItemProps) => {
  const {
    data: roomData
  } = useGetRoomDetailQuery(roomCode);
  const roomDetails = roomData?.data;
  const {
    data: currentTeamData
  } = useGetCurrentTeamQuery(undefined, {
    skip: !roomDetails?.teams || !roomDetails?.joined
  });
  const {
    tutorPanelOpen,
    vmPanelOpen
  } = useAppSelector(state => state.splitScreen);
  const isSplitScreenOpen = tutorPanelOpen || vmPanelOpen;
  const isEchoEnabled = useIsEchoEnabledInCurrentRoom();
  const shouldEnableAutoAnswer = !!autoAnswerQuestions && !!autoAnswerLabel;
  const {
    openTutorWithDelayedAction
  } = useOpenTutorWithDelayedAction(roomCode);
  const [postAnswer, {
    data: postAnswerResponse,
    reset: postAnswerReset,
    isLoading: isPostAnswerLoading
  }] = usePostAnswerMutation();
  const [, {
    data: resetRoomResponse
  }] = useResetUserProgressMutation({
    fixedCacheKey: 'shared-reset-progress'
  });
  const [hasUserAnsweredCorrectly, setHasUserAnsweredCorrectly] = useState<boolean>(false);
  const [incorrectAnswer, setIncorrectAnswer] = useState<string | null>(null);
  const [hintShown, setHintShown] = useState<boolean>(false);
  const [answer, setAnswer] = useState<string>('');
  const [submitButtonTitle, setSubmitButtonTitle] = useState<string>(SubmitButtonTitle.DEFAULT);
  const [answerPlaceholder, setAnswerPlaceholder] = useState<string>(`${props.progress?.answerDescription ?? ''}`);
  const doesQuestionRequireAnswer = answerPlaceholder !== AnswerPlaceholder.NO_ANSWER_NEEDED;
  const isAnswerInputDisabled = useMemo(() => answerPlaceholder === AnswerPlaceholder.NO_ANSWER_NEEDED || hasUserAnsweredCorrectly || answerPlaceholder === AnswerPlaceholder.LOGIN_TO_ANSWER || answerPlaceholder === AnswerPlaceholder.JOIN_THIS_ROOM, [answerPlaceholder, hasUserAnsweredCorrectly]);
  const [selectedOption, setSelectedOption] = useState<string>('');
  const isFeatureTransformOpenTasksMcqEnabled = useFeatureIsOn('transform-open-tasks-mcq');
  const shouldDisplayMcq = isFeatureTransformOpenTasksMcqEnabled || !isTaskBreakdownSupported;
  const areEchoHintsEnabled = useFeatureIsOn('echo-hints');
  const isChallengeRoom = roomType === RoomType.Challenge;
  const mcqEnabledQuestions = useMemo(() => {
    if (!shouldDisplayMcq || !roomCode) {
      return false;
    }
    const roomOptions = QUESTION_OPTIONS[(roomCode as keyof typeof QUESTION_OPTIONS)];
    if (!roomOptions) {
      return false;
    }
    const enabledQuestions: Record<string, boolean> = {};
    for (const [taskKey, taskQuestions] of Object.entries(roomOptions)) {
      for (const [questionKey, options] of Object.entries(taskQuestions)) {
        if (options?.length > 0) {
          const taskNo = taskKey.replace('task-', '');
          enabledQuestions[`${taskNo}-${questionKey}`] = true;
        }
      }
    }
    return enabledQuestions;
  }, [shouldDisplayMcq, roomCode]);
  const isTransformOpenEndedTasksMcqEnabled = useMemo(() => mcqEnabledQuestions && mcqEnabledQuestions[`${props.taskNo}-${questionNo}`], [mcqEnabledQuestions, props.taskNo, questionNo]);
  const {
    maskPlaceholder,
    fixedCharIndexes
  } = useAnswerMask({
    roomType,
    answerPlaceholder
  });
  const answerErrorMessage = useMemo(() => {
    if (incorrectAnswer === null) {
      return '';
    }
    const language = detectLanguage(incorrectAnswer);
    if (language !== 'eng' && language !== 'und') {
      return AnswerErrorMessage.NON_ENGLISH;
    }
    const length = maskPlaceholder?.length ?? 1;
    if (incorrectAnswer.length < length) {
      return AnswerErrorMessage.TOO_SHORT;
    }
    return AnswerErrorMessage.INCORRECT;
  }, [maskPlaceholder, incorrectAnswer]);

  // TODO [2024-03-01] - Insert glossary terms in question text by replacing [] below with glossaryData
  const refinedQuestion = useMemo(() => {
    let processedQuestion = question;
    if (isTransformOpenEndedTasksMcqEnabled && roomCode === 'offensivesecurityintro' && props.taskNo === 1) {
      processedQuestion = processedQuestion.replace(/<ul>[\S\s]*?<\/ul>/, '');
    }
    return replaceContent(processedQuestion, [], machineIP, attackBoxPrivateIP);
  }, [machineIP, question, attackBoxPrivateIP, roomCode, isTransformOpenEndedTasksMcqEnabled, props.taskNo]);
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    reset
  } = useForm({
    mode: 'onSubmit'
  });
  const {
    data: currentUserData,
    isSuccess: isUserLoggedIn
  } = useGetUserQuery();
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (postAnswerResponse?.data) {
      const {
        isCorrect,
        isRoomCompleted,
        isStreakIncreased,
        currentStreak
      } = postAnswerResponse.data;
      if (isCorrect) {
        dispatch(updateTaskQuestionState({
          taskNo: props.taskNo,
          questionNo
        }));
        setIncorrectAnswer(null);
        setSubmitButtonTitle(SubmitButtonTitle.CORRECT_ANSWER);
        if (!isTaskBreakdownSupported) {
          successNotification();
        }
        setHasUserAnsweredCorrectly(true);
        if (isStreakIncreased && currentStreak) {
          displayStreakNotification(currentStreak, currentUserData?.data?.user.streak.largestStreak);
        }
        if (isRoomCompleted) {
          dispatch(setIsRoomComplete(true));
        }
      } else {
        reset();
        if (isChallengeRoom) {
          errorNotification();
        } else {
          setIncorrectAnswer(answer);
        }
      }
      postAnswerReset();
    }
  }, [currentUserData?.data?.user.streak.largestStreak, dispatch, postAnswerReset, postAnswerResponse, props.taskNo, questionNo, reset, answer, isTransformOpenEndedTasksMcqEnabled, maskPlaceholder, isChallengeRoom, isTaskBreakdownSupported]);
  const processAnswerSubmission = useCallback(async () => {
    try {
      await postAnswer({
        answer,
        questionNo,
        roomCode,
        taskId
      }).unwrap();
    } catch (error) {
      NotificationDispatch('error', isQueryErrorWithMessage(error) ? error.data.message : 'An unknown error has occurred');
    }
  }, [answer, postAnswer, questionNo, roomCode, taskId]);
  const submitAnswerForRoomWithTaskBreakdownSupport = useCallback(async () => {
    // prevent empty submission for questions which require an answer
    if (hasUserJoinedRoom && !answer && doesQuestionRequireAnswer) {
      return;
    }
    await processAnswerSubmission();
  }, [answer, doesQuestionRequireAnswer, hasUserJoinedRoom, processAnswerSubmission]);
  const submitAnswer = useCallback(async () => {
    if (submitButtonTitle === SubmitButtonTitle.JOIN_THIS_ROOM) {
      await joinRoom();
    }
    if ([`${SubmitButtonTitle.DEFAULT}`, `${SubmitButtonTitle.COMPLETE}`].includes(submitButtonTitle) && roomCode) {
      await processAnswerSubmission();
    }
    if (submitButtonTitle === SubmitButtonTitle.LOGIN_TO_ANSWER) {
      navigate('/login');
    }
  }, [submitButtonTitle, roomCode, joinRoom, processAnswerSubmission, navigate]);
  useEffect(() => {
    if (resetRoomResponse?.status === 'success') {
      setAnswer('');
      setIncorrectAnswer(null);
    }
  }, [resetRoomResponse]);
  useEffect(() => {
    const handleTeamResetProgress = (data: unknown) => {
      const currentTeamId = currentTeamData?.data?._id;
      const {
        type,
        teamId
      } = (data as RoomTeamEvent);
      if (type === 'reset-progress' && !!currentTeamId) {
        if (teamId === currentTeamId) setAnswer('');
        invalidateResetProgress();
      }
    };
    addSocketListener(ROOM_TEAM_EVENT, handleTeamResetProgress);
    return () => {
      removeSocketListener(ROOM_TEAM_EVENT);
    };
  }, [currentTeamData?.data?._id]);
  const onOpenHint = useCallback(() => {
    if (submitButtonTitle === (SubmitButtonTitle.LOGIN_TO_ANSWER as string)) {
      navigate('/login');
      return;
    }
    if (isEchoEnabled && areEchoHintsEnabled) {
      openTutorWithDelayedAction({
        type: 'hint',
        payload: {
          defaultHint: hint,
          taskId,
          questionNo
        }
      });
    } else {
      setHintShown(true);
    }
  }, [navigate, submitButtonTitle, hint, isEchoEnabled, taskId, questionNo, openTutorWithDelayedAction, areEchoHintsEnabled]);
  const determineSubmitButtonTitle = useCallback(() => {
    setSubmitButtonTitle(SubmitButtonTitle.DEFAULT);
    if (!isUserLoggedIn) setSubmitButtonTitle(SubmitButtonTitle.LOGIN_TO_ANSWER);else if (isUserLoggedIn && !hasUserJoinedRoom) setSubmitButtonTitle(SubmitButtonTitle.JOIN_THIS_ROOM);else if (isUserLoggedIn && hasUserJoinedRoom && answerPlaceholder === AnswerPlaceholder.NO_ANSWER_NEEDED && !hasUserAnsweredCorrectly) setSubmitButtonTitle(SubmitButtonTitle.COMPLETE);else if (isUserLoggedIn && hasUserJoinedRoom && hasUserAnsweredCorrectly) setSubmitButtonTitle(SubmitButtonTitle.CORRECT_ANSWER);
  }, [answerPlaceholder, hasUserAnsweredCorrectly, isUserLoggedIn, hasUserJoinedRoom]);
  const determineAnswerPlaceholder = useCallback(() => {
    setAnswerPlaceholder(`${props.progress?.answerDescription ?? ''}`);
    if (!isUserLoggedIn) setAnswerPlaceholder((AnswerPlaceholder.LOGIN_TO_ANSWER as string));else if (isUserLoggedIn && !hasUserJoinedRoom) setAnswerPlaceholder(AnswerPlaceholder.JOIN_THIS_ROOM);
  }, [props.progress?.answerDescription, isUserLoggedIn, hasUserJoinedRoom]);
  useEffect(() => {
    determineAnswerPlaceholder();
    determineSubmitButtonTitle();
  }, [props.progress?.answerDescription, determineAnswerPlaceholder, determineSubmitButtonTitle, isUserLoggedIn, hasUserAnsweredCorrectly]);
  useEffect(() => {
    setHasUserAnsweredCorrectly(Boolean(props.progress?.correct));
    if (props.progress?.correct) {
      const options = getQuestionOptions(roomCode, props.taskNo, questionNo);
      const matchingOption = options.find(opt => isSameAnswer(opt.value, props.progress?.submission ?? ''));
      const correctCaseAnswer = matchingOption?.value ?? props.progress?.submission ?? '';
      setAnswer(correctCaseAnswer);
      setSelectedOption(correctCaseAnswer);
    } else if (isTaskBreakdownSupported) {
      setAnswer('');
    }
  }, [props.progress, roomCode, props.taskNo, questionNo, isTaskBreakdownSupported]);
  useEffect(() => {
    const handleAutomaticallyAnswerQuestion = (data: {
      taskId: string;
      questionNo: number;
      answer: string;
      isRoomCompleted: boolean;
    }) => {
      const {
        taskId: receivedTaskId,
        questionNo: receivedQuestionNo,
        answer: receivedAnswer,
        isRoomCompleted
      } = data;
      if (receivedTaskId === taskId && receivedQuestionNo === questionNo) {
        setHasUserAnsweredCorrectly(true);
        setAnswer(receivedAnswer);
        invalidateResetProgress();
        if (isRoomCompleted) {
          setTimeout(() => {
            dispatch(setIsRoomComplete(true));
          }, 1500);
        }
      }
    };
    addSocketListener(TutorSocketEventType.AUTOMATICALLY_ANSWER_QUESTION, handleAutomaticallyAnswerQuestion);
    return () => {
      removeSocketListener(TutorSocketEventType.AUTOMATICALLY_ANSWER_QUESTION);
    };
  }, [questionNo, taskId, dispatch, setAnswer, setHasUserAnsweredCorrectly]);
  const textFieldProps = useMemo(() => ({
    ...register(`${questionNo}`),
    id: `${questionNo}`,
    'data-testid': 'answer-field',
    autoComplete: 'off',
    placeholder: answerPlaceholder,
    isDisabled: isAnswerInputDisabled,
    value: answer
  }), [answer, answerPlaceholder, isAnswerInputDisabled, questionNo, register]);
  const renderInputField = (): JSX.Element => {
    if (isTransformOpenEndedTasksMcqEnabled) {
      const options = getQuestionOptions(roomCode, props.taskNo, questionNo);
      const questionGroupName = `task-${taskId}-q-${questionNo}`;
      if (options.length > 0) {
        return <StyledRadioSelectionWrapper $disabled={isAnswerInputDisabled}>
            {options.map(option => <RadioInputGroup key={option.value} option={{
            value: option.value,
            label: option.label,
            name: questionGroupName
          }} currentOption={selectedOption} onChange={e => {
            setSelectedOption(e.target.value);
            setAnswer(e.target.value);
            setIncorrectAnswer(null);
          }} isError={!hasUserAnsweredCorrectly && isSameAnswer(incorrectAnswer ?? '', option.value)} errorMessage={!hasUserAnsweredCorrectly && isSameAnswer(incorrectAnswer ?? '', option.value) ? 'Your answer is incorrect.' : undefined} disabled={isAnswerInputDisabled} isTaskBreakdownSupported={isTaskBreakdownSupported} />)}
          </StyledRadioSelectionWrapper>;
      }
    }
    return <StyledTextfieldContainer $splitScreenOpen={isSplitScreenOpen} data-sentry-element="StyledTextfieldContainer" data-sentry-component="renderInputField" data-sentry-source-file="question-and-answer-item.tsx">
        {maskPlaceholder ? <MaskedFormInput {...textFieldProps} maskPlaceholder={maskPlaceholder} errorMessage={answerErrorMessage} onTypedValueChange={(value: string) => {
        setAnswer(value);
      }} fixedCharIndexes={fixedCharIndexes} /> : <TextField {...textFieldProps} variant="input" errorMessage={answerErrorMessage} onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
        setAnswer(event.target.value);
      }} />}
      </StyledTextfieldContainer>;
  };
  const renderHintButton = () => <>
      <StyledHintButton variant={isUserLoggedIn && hasUserJoinedRoom && hasUserAnsweredCorrectly ? 'solid' : 'outlined'} color="hint" onClick={onOpenHint} isLoading={false} data-sentry-element="StyledHintButton" data-sentry-source-file="question-and-answer-item.tsx">
        <FontAwesomeIcon icon={faLightbulb} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="question-and-answer-item.tsx" />
        Hint
      </StyledHintButton>
      <QuestionHint hint={hasUserJoinedRoom ? hint : 'Join this room to view hint and answer questions.'} isSlidingPaneOpen={hintShown} setIsSlidingPaneOpen={setHintShown} data-sentry-element="QuestionHint" data-sentry-source-file="question-and-answer-item.tsx" />
    </>;
  const renderSubmitButton = () => {
    if (shouldEnableAutoAnswer) {
      return null;
    }
    const getButtonTitle = () => {
      if (!isUserLoggedIn) {
        return 'Login';
      }
      if (!hasUserJoinedRoom) {
        return 'Join Room';
      }
      if (isLastTask) {
        return 'Complete room';
      }
      if (hasUserAnsweredCorrectly) {
        return 'Continue';
      }
      return 'Submit answer';
    };
    const handleClick = () => {
      if (!isUserLoggedIn) {
        return navigate('/login');
      }
      if (!hasUserJoinedRoom) {
        return joinRoom();
      }
      if (isLastTask || !hasUserAnsweredCorrectly) {
        return submitAnswerForRoomWithTaskBreakdownSupport();
      }
      return advanceToNextTask?.();
    };
    if (isTaskBreakdownSupported) {
      const containerElement = document.querySelector(`#room-footer-submit-btn`);
      if (!containerElement) {
        return null;
      }
      return createPortal(<StyledButton
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onClick={handleClick} variant="solid" type="submit" color="primary" isLoading={false} isDisabled={isTransformOpenEndedTasksMcqEnabled && !selectedOption || isTaskBreakdownSupported && (isPostAnswerLoading || hasUserJoinedRoom && !answer && doesQuestionRequireAnswer)}>
          {getButtonTitle()}
        </StyledButton>, containerElement);
    }
    return <StyledButton variant={isUserLoggedIn && hasUserJoinedRoom && hasUserAnsweredCorrectly ? 'solid' : 'outlined'} type="submit" color={isUserLoggedIn && hasUserJoinedRoom ? 'primary' : 'add'} isLoading={isPostAnswerLoading} isQuestionDone={hasUserJoinedRoom ? hasUserAnsweredCorrectly : false} isDisabled={isPostAnswerLoading || isTransformOpenEndedTasksMcqEnabled && !selectedOption} data-sentry-element="StyledButton" data-sentry-component="renderSubmitButton" data-sentry-source-file="question-and-answer-item.tsx">
        {isUserLoggedIn && hasUserJoinedRoom && <FontAwesomeIcon icon={hasUserAnsweredCorrectly ? faCheck : faPaperPlane} />}
        {submitButtonTitle}
      </StyledButton>;
  };
  return <StyledQuestion data-sentry-element="StyledQuestion" data-sentry-component="QuestionAndAnswerItem" data-sentry-source-file="question-and-answer-item.tsx">
      <StyledExtraPointsTagAndQuestionContainer data-sentry-element="StyledExtraPointsTagAndQuestionContainer" data-sentry-source-file="question-and-answer-item.tsx">
        {isUserLoggedIn && hasUserJoinedRoom && !!props?.progress?.extraPoints && props.progress.extraPoints > 0 && <StyledTagContainer>
            <StyledTag size="small" color="primary" variation="main">
              <StyledPoints>{`+ ${' '} ${props?.progress?.extraPoints}`}</StyledPoints>
            </StyledTag>
          </StyledTagContainer>}
        {!shouldEnableAutoAnswer && refinedQuestion.length > 0 && <StyledQuestionContainer dangerouslySetInnerHTML={{
        __html: refinedQuestion
      }} />}
      </StyledExtraPointsTagAndQuestionContainer>
      <StyledContainer data-sentry-element="StyledContainer" data-sentry-source-file="question-and-answer-item.tsx">
        <StyledForm
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSubmit={handleSubmit(isTaskBreakdownSupported ? submitAnswerForRoomWithTaskBreakdownSupport : submitAnswer)} $isTransformOpenEndedTasksMcqEnabled={isTransformOpenEndedTasksMcqEnabled} $splitScreenOpen={isSplitScreenOpen} data-sentry-element="StyledForm" data-sentry-source-file="question-and-answer-item.tsx">
          {shouldEnableAutoAnswer ? <StyledAutoAnswerContainer data-testid="auto-answer-container" data-state={hasUserAnsweredCorrectly ? 'answered' : 'unanswered'}>
              <AutoAnswerIcon correct={hasUserAnsweredCorrectly} isFirstNonAnsweredQuestion={!!isFirstNonAnsweredQuestion} />
              <AutoAnswerLabel label={hasUserAnsweredCorrectly ? answer : autoAnswerLabel} correct={hasUserAnsweredCorrectly} />
            </StyledAutoAnswerContainer> : renderInputField()}
          {(!shouldEnableAutoAnswer || hint) && <StyledButtonsContainer $isTaskBreakdownSupported={!!isTaskBreakdownSupported}>
              {renderSubmitButton()}
              {hint && renderHintButton()}
            </StyledButtonsContainer>}
        </StyledForm>
      </StyledContainer>
    </StyledQuestion>;
};